import React, { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, CreditCard, Download, FileText, RotateCcw, Truck,
} from 'lucide-react';
import {
  Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { useXomicsAuth } from './AuthContext';
import { downloadFinanceLedger, loadFinanceLedger, loadFinanceOptions, loadFinanceSummary, openCustomerDocument } from './api';
import { ChartKey } from './DashboardParts';
import { CHART, ChartTip } from './MobileParts';
import { Empty, Failed, Head, Loading, Pill, SearchField, WorkspaceTabs, XomicsSelect } from './parts';
import { errorMessage } from './ui';
import { useLiveResource } from './useLive';
import { useExchangeRates } from './viewMoney';
import { tx } from './i18n';
import './organizations.css';
import './finance.css';

// Finance: every movement of money the company has, in one place.
//
// Overview: what came in and went out in a window, in one currency the reader chooses (their
// profile's view currency to begin with), at the European Central Bank's rates; by place (the
// office that runs a sale's country, the laboratory a customer's samples go to, the place a parcel
// left), by channel, by market and product; what customers still owe, and how late. Transactions:
// the movements themselves (store payments and refunds, customer invoices, label costs), filtered
// any way, as a CSV file too. Receivables: customers' open invoices by age, and who owes what.
// Shipping costs: what labels cost, by carrier, place and what they were for, beside what orders
// were charged for shipping. Amounts stay in the currency they moved in; a total is converted and
// says so. A currency without a rate is left out of a total and named.
const f = (key, values) => tx(`ui.finance.${key}`, values);
const DAY = 24 * 60 * 60 * 1000;
const PERIODS = Object.freeze([7, 30, 90, 365]);
const KIND_ICONS = Object.freeze({ payment: CreditCard, refund: RotateCcw, invoice: FileText, shipping: Truck });
const STATUS_TONES = Object.freeze({
  succeeded: 'ok', paid: 'ok', delivered: 'ok', canceled: 'bad', cancelled: 'bad', lost: 'bad', overdue: 'bad', refunded: 'warn',
  due: 'warn', requires_payment_method: 'flat', requires_confirmation: 'flat', requires_action: 'warn', processing: 'info', requires_capture: 'info',
  created: 'flat', label_printed: 'info', dispatched: 'info', in_transit: 'info', returned: 'warn',
});
const CHANNELS = Object.freeze(['storefront', 'mobile', 'portal', 'customer']);
const AGES = Object.freeze(['current', 'd30', 'd60', 'd90', 'older']);
const INK = CHART.ink;
const MID = CHART.mid;
const WARM = '#d9957f';
const AXIS = { tickLine: false, axisLine: false, tick: CHART.tick };

const NUMBER = new Intl.NumberFormat();
const PERCENT = new Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 1 });
const dayLabel = (ms) => new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' }).format(new Date(ms));
const longDay = (ms) => new Intl.DateTimeFormat(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(ms));
const timeLabel = (ms) => new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' }).format(new Date(ms));
const rateDay = (ms) => new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(ms));
const countryName = (code) => {
  try { return new Intl.DisplayNames(undefined, { type: 'region' }).of(code) || code; } catch (_error) { return code; }
};

// `compact` shortens large amounts and drops minor units, as a chart's axis shows money.
function money(minor, currency, minorUnit = 2, { compact = false } = {}) {
  const value = Number(minor) / 10 ** minorUnit;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency, currencyDisplay: 'narrowSymbol',
      ...(compact ? (Math.abs(value) >= 10000 ? { notation: 'compact', maximumFractionDigits: 1 } : { minimumFractionDigits: 0, maximumFractionDigits: 0 })
        : { minimumFractionDigits: minorUnit, maximumFractionDigits: minorUnit }),
    }).format(value);
  } catch (_error) {
    return `${value.toFixed(minorUnit)} ${currency}`;
  }
}

// Turns an amount in its currency into the chosen one, at the bank's rate; null without a rate.
function useConverter(target) {
  const rates = useExchangeRates(true);
  return useMemo(() => {
    const byCode = new Map((rates.data?.currencies ?? []).map((currency) => [currency.code, currency]));
    const to = byCode.get(target) ?? null;
    const minorUnit = to?.minorUnit ?? 2;
    const missing = new Set();
    function convert(minor, currency, fromMinorUnit = byCode.get(currency)?.minorUnit ?? 2) {
      if (!minor) return 0;
      if (currency === target) return Number(minor) / 10 ** fromMinorUnit * 10 ** minorUnit;
      const from = byCode.get(currency);
      if (!to || to.unitsPerEur === null || !from || from.unitsPerEur === null) { missing.add(currency); return null; }
      return (Number(minor) / 10 ** fromMinorUnit / from.unitsPerEur) * to.unitsPerEur * 10 ** minorUnit;
    }
    // A sum over rows of { currency, [field] }, in the chosen currency; what had no rate is left out.
    function sum(rows, field) {
      return rows.reduce((total, row) => total + (convert(row[field], row.currency) ?? 0), 0);
    }
    const format = (minor, options) => money(Math.round(minor), target, minorUnit, options);
    return { ready: rates.data !== null, error: rates.error, convert, sum, format, missing, minorUnit, rateDate: rates.data?.rateDate ?? null };
  }, [rates.data, rates.error, target]);
}

// The window a period covers, ending now.
function windowOf(days) {
  const to = Date.now() + 1;
  return { from: to - days * DAY, to };
}

// A place by its name and city (our offices share one name), and under it its legal entity and code.
function placeTitle(place) {
  if (!place) return f('no_place');
  return place.city && !place.name.includes(place.city) ? `${place.name}, ${place.city}` : place.name;
}
function placeSub(place) {
  if (!place) return f('no_place_hint');
  return [place.legalName, place.code].filter(Boolean).join(' · ');
}
function placeLabel(place) {
  if (!place) return f('no_place');
  return place.legalName ? `${placeTitle(place)} · ${place.legalName}` : placeTitle(place);
}

function share(value, total) {
  return total > 0 ? Math.max(0, Math.min(1, value / total)) : 0;
}

// ------------------------------------------------------------------ the page's head

function PeriodTabs({ days, onChange }) {
  return <WorkspaceTabs className="xo-economics-range-tabs" value={days} onChange={onChange} label={f('period')}
    tabs={PERIODS.map((value) => ({ value, label: value === 365 ? tx('ui.economics.year') : tx('ui.economics.days', { count: value }) }))} />;
}

function ShowIn({ value, options, onChange }) {
  return <label className="xo-fin-showin"><span>{f('show_in')}</span>
    <XomicsSelect value={value} onChange={(event) => onChange(event.target.value)} aria-label={f('show_in')}>
      {options.map((currency) => <option key={currency.code} value={currency.code}>{currency.code} · {currency.name}</option>)}
    </XomicsSelect></label>;
}

function RateNote({ converter, target }) {
  if (converter.error) return <p className="xo-fin-note t-warn">{tx('ui.money.rates_missing')}</p>;
  const missing = [...converter.missing].filter((code) => code !== target);
  return <p className="xo-fin-note">
    {f('converted_note', { currency: target, date: converter.rateDate ? rateDay(converter.rateDate) : '' })}
    {missing.length > 0 && <span className="t-warn"> {f('no_rate_for', { currencies: missing.join(', ') })}</span>}
  </p>;
}

// ------------------------------------------------------------------ overview

function Window({ big, cells }) {
  return <section className="xo-transaction-window xo-fin-window">
    <div className="xo-transaction-window-total"><span>{big.label}</span><strong>{big.value}</strong>{big.note && <small>{big.note}</small>}</div>
    <dl>{cells.map((cell) => <div key={cell.label}><dt>{cell.label}</dt><dd>{cell.value}</dd>
      {cell.note && <small className={cell.tone ? `t-${cell.tone}` : ''}>{cell.note}</small>}</div>)}</dl>
  </section>;
}

function Metric({ label, value, note }) {
  return <div className="xo-fin-metric"><span>{label}</span><strong>{value}</strong>{note && <small>{note}</small>}</div>;
}

function Card({ title, detail, action, children, className = '' }) {
  return <section className={`xo-fin-card ${className}`}>
    <header><div><h2>{title}</h2>{detail && <p>{detail}</p>}</div>{action}</header>
    {children}
  </section>;
}

// Rows with a bar for each one's share of the largest.
function Bars({ rows, format, empty }) {
  const top = Math.max(0, ...rows.map((row) => row.value));
  if (rows.length === 0 || top === 0) return <p className="xo-fin-quiet">{empty}</p>;
  return <ol className="xo-fin-bars">{rows.map((row) => <li key={row.key}>
    <span className="xo-fin-bar-name"><strong>{row.label}</strong>{row.sub && <small>{row.sub}</small>}</span>
    <span className="xo-fin-bar-track" aria-hidden="true"><i style={{ width: `${share(row.value, top) * 100}%` }} /></span>
    <span className="xo-fin-bar-value">{format(row.value)}{row.note && <small>{row.note}</small>}</span>
  </li>)}</ol>;
}

// The facts of a window, filtered by place and channel, then added up in the chosen currency.
function useFigures(summary, options, converter, { place, channel }) {
  return useMemo(() => {
    if (!summary) return null;
    const inPlace = (row) => place === '' || (place === 'none' ? row.officeId === null : row.officeId === Number(place));
    const storeChannel = (row) => channel === '' || row.source === channel;
    const customerChannel = channel === '' || channel === 'customer';
    const sales = summary.sales.filter((row) => inPlace(row) && storeChannel(row));
    const orders = summary.orders.filter((row) => inPlace(row) && storeChannel(row));
    const invoices = customerChannel ? summary.invoices.filter(inPlace) : [];
    const invoicePayments = customerChannel ? summary.invoicePayments.filter(inPlace) : [];
    const receivables = summary.receivables.filter(inPlace);
    const shipping = summary.shipping.filter(inPlace);
    const products = channel === '' || channel !== 'customer' ? summary.products : [];
    const S = (rows, field) => converter.sum(rows, field);
    const collected = S(sales, 'collectedMinor');
    const refunded = S(sales, 'refundedMinor');
    const storeNet = collected - refunded;
    const invoicePaid = S(invoicePayments, 'paidMinor');
    const issued = S(invoices, 'issuedMinor');
    const open = S(receivables, 'openMinor');
    const overdue = S(receivables.filter((row) => row.bucket !== 'current'), 'openMinor');
    const shippingCost = S(shipping, 'costMinor') + S(shipping, 'returnMinor');
    const shippingCharged = S(orders, 'shippingMinor');
    const succeeded = sales.reduce((n, row) => n + row.succeeded, 0);
    const paidOrders = orders.reduce((n, row) => n + row.orders, 0);
    const places = new Map(options.places.map((entry) => [entry.id, entry]));
    // By place: store sales, customer invoices paid, label costs.
    const byPlace = new Map();
    const placeRow = (id) => {
      const key = id === null ? 'none' : String(id);
      if (!byPlace.has(key)) byPlace.set(key, { key, place: id === null ? null : places.get(id) ?? null, store: 0, customers: 0, shipping: 0 });
      return byPlace.get(key);
    };
    for (const row of sales) placeRow(row.officeId).store += (converter.convert(row.collectedMinor - row.refundedMinor, row.currency) ?? 0);
    for (const row of invoicePayments) placeRow(row.officeId).customers += (converter.convert(row.paidMinor, row.currency) ?? 0);
    for (const row of shipping) placeRow(row.officeId).shipping += (converter.convert(row.costMinor + row.returnMinor, row.currency) ?? 0);
    const channels = CHANNELS.map((code) => ({
      key: code, label: f(`channel.${code}`),
      value: code === 'customer' ? (customerChannel ? invoicePaid : 0)
        : S(sales.filter((row) => row.source === code), 'collectedMinor') - S(sales.filter((row) => row.source === code), 'refundedMinor'),
    })).filter((row) => channel === '' || row.key === channel);
    const markets = new Map();
    for (const row of sales) {
      const entry = markets.get(row.country) || { key: row.country, label: countryName(row.country), sub: row.country, value: 0, orders: 0 };
      entry.value += (converter.convert(row.collectedMinor - row.refundedMinor, row.currency) ?? 0);
      entry.orders += row.succeeded;
      markets.set(row.country, entry);
    }
    const productRows = new Map();
    for (const row of products) {
      const entry = productRows.get(row.productId) || { key: row.productId, label: row.name, sub: row.code, value: 0, units: 0 };
      entry.value += (converter.convert(row.salesMinor, row.currency) ?? 0);
      entry.units += row.units;
      productRows.set(row.productId, entry);
    }
    // Day by day: store net, invoices paid, labels.
    const days = new Map();
    const dayRow = (ms) => { if (!days.has(ms)) days.set(ms, { at: ms, store: 0, customers: 0, shipping: 0 }); return days.get(ms); };
    for (let at = Math.floor(summary.from / DAY) * DAY; at < summary.to; at += DAY) dayRow(at);
    for (const row of sales) dayRow(row.day).store += (converter.convert(row.collectedMinor - row.refundedMinor, row.currency) ?? 0);
    for (const row of invoicePayments) dayRow(row.day).customers += (converter.convert(row.paidMinor, row.currency) ?? 0);
    for (const row of shipping) dayRow(row.day).shipping += (converter.convert(row.costMinor + row.returnMinor, row.currency) ?? 0);
    const ages = AGES.map((bucket) => {
      const count = receivables.filter((row) => row.bucket === bucket).reduce((n, row) => n + row.invoices, 0);
      return { key: bucket, label: f(`age.${bucket}`), value: S(receivables.filter((row) => row.bucket === bucket), 'openMinor'), count, note: NUMBER.format(count) };
    });
    const debtors = new Map();
    for (const row of summary.debtors) {
      const entry = debtors.get(row.organizationId) || { key: row.organizationId, id: row.organizationId, label: row.name, sub: row.code, value: 0, overdue: 0, invoices: 0 };
      entry.value += (converter.convert(row.openMinor, row.currency) ?? 0);
      entry.overdue += row.overdue;
      entry.invoices += row.invoices;
      debtors.set(row.organizationId, entry);
    }
    const carriers = new Map();
    const purposes = new Map();
    for (const row of shipping) {
      const cost = converter.convert(row.costMinor + row.returnMinor, row.currency) ?? 0;
      const carrier = carriers.get(row.carrier) || { key: row.carrier, label: row.carrier, value: 0, count: 0 };
      carrier.value += cost; carrier.count += row.shipments; carriers.set(row.carrier, carrier);
      const purpose = purposes.get(row.purpose) || { key: row.purpose, label: f(`purpose.${row.purpose}`), value: 0, count: 0 };
      purpose.value += cost; purpose.count += row.shipments; purposes.set(row.purpose, purpose);
    }
    const labels = shipping.reduce((n, row) => n + row.shipments, 0);
    return {
      collected, refunded, storeNet, invoicePaid, issued, open, overdue, shippingCost, shippingCharged, succeeded, paidOrders, labels,
      invoicesIssued: invoices.reduce((n, row) => n + row.invoices, 0),
      refunds: sales.filter((row) => row.refundedMinor > 0).length,
      discount: S(orders, 'discountMinor'), tax: S(orders, 'taxMinor'), ordersTotal: S(orders, 'totalMinor'),
      byPlace: [...byPlace.values()].sort((a, b) => (b.store + b.customers) - (a.store + a.customers)),
      channels, markets: [...markets.values()].sort((a, b) => b.value - a.value).slice(0, 8),
      products: [...productRows.values()].sort((a, b) => b.value - a.value).slice(0, 8),
      daily: [...days.values()].sort((a, b) => a.at - b.at),
      ages, debtors: [...debtors.values()].sort((a, b) => b.value - a.value).slice(0, 8),
      carriers: [...carriers.values()].sort((a, b) => b.value - a.value),
      purposes: [...purposes.values()].sort((a, b) => b.value - a.value),
    };
  }, [channel, converter, options, place, summary]);
}

function MoneyChart({ daily, converter, target }) {
  const fmt = (minor) => converter.format(minor);
  return <div className="xo-fin-chart">
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={daily} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke={CHART.grid} />
        <XAxis dataKey="at" tickFormatter={dayLabel} minTickGap={28} {...AXIS} />
        <YAxis tickFormatter={(value) => converter.format(value, { compact: true })} width={70} {...AXIS} />
        <Tooltip cursor={CHART.cursor} animationDuration={120} content={(
          <ChartTip title={(label) => ({ main: longDay(Number(label)) })} rows={(point) => [
            { name: f('series.store'), color: INK, mark: 'bar', text: fmt(point.store) },
            { name: f('series.customers'), color: MID, mark: 'bar', text: fmt(point.customers) },
            { name: f('series.shipping'), color: WARM, mark: 'line', text: point.shipping > 0 ? fmt(point.shipping) : null },
          ]} />
        )} />
        <Bar dataKey="store" stackId="in" fill={INK} maxBarSize={18} />
        <Bar dataKey="customers" stackId="in" fill={MID} radius={[3, 3, 0, 0]} maxBarSize={18} />
        <Line dataKey="shipping" type="monotone" stroke={WARM} strokeWidth={2} dot={false} />
      </ComposedChart>
    </ResponsiveContainer>
    <ChartKey items={[{ label: f('series.store_in', { currency: target }), color: INK }, { label: f('series.customers'), color: MID },
      { label: f('series.shipping'), color: WARM, line: true }]} />
  </div>;
}

function PlaceTable({ rows, converter }) {
  if (rows.length === 0) return <p className="xo-fin-quiet">{f('nothing_in_window')}</p>;
  const fmt = (minor) => converter.format(minor);
  return <div className="xo-fin-table-wrap"><table className="xo-fin-table">
    <thead><tr><th>{f('place')}</th><th className="n">{f('col.store')}</th><th className="n">{f('col.customers')}</th>
      <th className="n">{f('col.shipping')}</th><th className="n">{f('col.after_shipping')}</th></tr></thead>
    <tbody>{rows.map((row) => <tr key={row.key}>
      <td><strong>{placeTitle(row.place)}</strong><small>{placeSub(row.place)}</small></td>
      <td className="n">{fmt(row.store)}</td><td className="n">{fmt(row.customers)}</td><td className={`n${row.shipping ? ' t-out' : ''}`}>{row.shipping ? `−${fmt(row.shipping)}` : fmt(0)}</td>
      <td className="n"><strong>{fmt(row.store + row.customers - row.shipping)}</strong></td>
    </tr>)}</tbody>
  </table></div>;
}

function Overview({ figures, converter, target }) {
  const fmt = (minor) => converter.format(minor);
  return <>
    <Window big={{ label: f('net_in'), value: fmt(figures.storeNet + figures.invoicePaid),
      note: f('net_in_note', { store: fmt(figures.storeNet), customers: fmt(figures.invoicePaid) }) }} cells={[
      { label: f('invoiced'), value: fmt(figures.issued), note: f('invoiced_note', { count: NUMBER.format(figures.invoicesIssued) }) },
      { label: f('receivables'), value: fmt(figures.open), note: figures.overdue > 0 ? f('overdue_part', { amount: fmt(figures.overdue) }) : f('none_overdue'),
        tone: figures.overdue > 0 ? 'bad' : null },
      { label: f('shipping_costs'), value: fmt(figures.shippingCost), note: f('labels_count', { count: NUMBER.format(figures.labels) }) },
    ]} />
    <div className="xo-fin-metrics">
      <Metric label={f('paid_orders')} value={NUMBER.format(figures.paidOrders)} note={figures.paidOrders ? f('average', { amount: fmt(figures.ordersTotal / figures.paidOrders) }) : null} />
      <Metric label={tx('ui.economics.collected')} value={fmt(figures.collected)}
        note={f('orders_count', { count: NUMBER.format(figures.succeeded) })} />
      <Metric label={f('refunds')} value={fmt(figures.refunded)} note={f('refund_count', { count: NUMBER.format(figures.refunds) })} />
      <Metric label={f('discounts')} value={fmt(figures.discount)} note={f('discounts_note')} />
      <Metric label={f('tax')} value={fmt(figures.tax)} note={f('tax_note')} />
      <Metric label={f('shipping_cover')} value={figures.shippingCost ? PERCENT.format(figures.shippingCharged / figures.shippingCost) : '0%'}
        note={f('shipping_cover_note', { charged: fmt(figures.shippingCharged) })} />
    </div>
    <Card title={f('money_in_out')} detail={f('money_in_out_detail')}><MoneyChart daily={figures.daily} converter={converter} target={target} /></Card>
    <div className="xo-fin-two">
      <Card title={f('by_place')} detail={f('by_place_detail')}><PlaceTable rows={figures.byPlace} converter={converter} /></Card>
      <Card title={f('by_channel')} detail={f('by_channel_detail')}><Bars rows={figures.channels} format={fmt} empty={f('nothing_in_window')} /></Card>
    </div>
    <div className="xo-fin-two">
      <Card title={f('by_market')} detail={f('by_market_detail')}>
        <Bars rows={figures.markets.map((row) => ({ ...row, note: f('orders_count', { count: NUMBER.format(row.orders) }) }))} format={fmt} empty={f('nothing_in_window')} /></Card>
      <Card title={f('top_products')} detail={f('top_products_detail')}>
        <Bars rows={figures.products.map((row) => ({ ...row, note: f('units', { count: NUMBER.format(row.units) }) }))} format={fmt} empty={f('nothing_in_window')} /></Card>
    </div>
    <div className="xo-fin-two">
      <Card title={f('ages')} detail={f('ages_detail')}
        action={<Link className="xo-btn-2" to="/xomics/finance/receivables">{f('see_receivables')}</Link>}>
        <Bars rows={figures.ages} format={fmt} empty={f('nothing_owed')} /></Card>
      <Card title={f('debtors')} detail={f('debtors_detail')}>
        <Bars rows={figures.debtors.map((row) => ({ ...row, note: row.overdue ? f('overdue_invoices', { count: row.overdue }) : f('open_invoices', { count: row.invoices }) }))}
          format={fmt} empty={f('nothing_owed')} /></Card>
    </div>
  </>;
}

// ------------------------------------------------------------------ the ledger

function useOrderScope() {
  const { permissions } = useXomicsAuth();
  if (!permissions.includes('ORDER_READ')) return null;
  return permissions.includes('ORDER_READ_ALL') ? 'global' : 'local';
}

function MovementRow({ movement, place, converter, target, orderScope, onError }) {
  const Icon = KIND_ICONS[movement.kind] ?? CreditCard;
  const out = movement.amountMinor < 0;
  const moneyIn = movement.kind === 'payment' ? movement.status === 'succeeded' : movement.kind === 'invoice' ? movement.status === 'paid' : false;
  const converted = movement.currency === target ? null : converter.convert(movement.amountMinor, movement.currency, movement.minorUnit);
  const counts = movement.kind !== 'payment' || movement.status === 'succeeded';
  const linkTo = (movement.kind === 'payment' || movement.kind === 'refund') && orderScope
    ? `/xomics/orders?scope=${encodeURIComponent(orderScope)}&orderUuid=${encodeURIComponent(movement.uuid)}` : null;
  const extra = movement.kind === 'payment' && movement.extraMinor > 0 ? f('refunded_part', { amount: money(movement.extraMinor, movement.currency, movement.minorUnit) })
    : movement.kind === 'shipping' && movement.extraMinor !== null ? f('charged', { amount: money(movement.extraMinor, movement.extraCurrency, 2) })
      : movement.kind === 'invoice' ? (movement.paidAt ? f('paid_on', { date: dayLabel(movement.paidAt) }) : f('due_on', { date: dayLabel(movement.dueAt) })) : null;
  const body = <>
    <span className={`xo-fin-marker is-${movement.kind}${moneyIn ? ' is-in' : ''}${out ? ' is-out' : ''}`} aria-hidden="true"><Icon /></span>
    <span className="xo-fin-cell"><strong>{movement.reference}</strong>
      <small>{f(`kind.${movement.kind}`)}{movement.detail ? ` · ${movement.kind === 'shipping' && movement.source === 'order' ? f('for_order', { ref: movement.detail })
        : movement.kind === 'shipping' && movement.source === 'stock_request' ? f('for_request', { ref: movement.detail }) : movement.detail}` : ''}</small></span>
    <span className="xo-fin-cell"><strong>{movement.who || f('no_name')}</strong><small>{f(`channel.${movement.source}`)}</small></span>
    <span className="xo-fin-cell"><strong>{placeTitle(place)}</strong><small>{placeSub(place)}</small></span>
    <span className="xo-fin-cell"><strong>{movement.country ? countryName(movement.country) : ''}</strong><small>{movement.currency}</small></span>
    <span className="xo-fin-cell"><Pill tone={STATUS_TONES[movement.status] || 'flat'}>{f(`status.${movement.status}`)}</Pill>{extra && <small>{extra}</small>}</span>
    <span className={`xo-fin-cell is-money${out ? ' t-out' : ''}${counts ? '' : ' is-muted'}`}>
      <strong>{out ? '−' : ''}{money(Math.abs(movement.amountMinor), movement.currency, movement.minorUnit)}</strong>
      {converted !== null && <small>≈ {out ? '−' : ''}{converter.format(Math.abs(converted))}</small>}</span>
    <time className="xo-fin-time" dateTime={new Date(movement.at).toISOString()}>{timeLabel(movement.at)}</time>
  </>;
  if (linkTo) return <li><Link className="xo-fin-entry is-linked" to={linkTo}>{body}<ChevronRight className="xo-fin-open" aria-hidden="true" /></Link></li>;
  if (movement.kind === 'invoice') {
    return <li><button type="button" className="xo-fin-entry is-linked" onClick={() => openCustomerDocument('invoice', movement.uuid).catch((caught) => onError(errorMessage(caught)))}>
      {body}<ChevronRight className="xo-fin-open" aria-hidden="true" /></button></li>;
  }
  return <li><div className="xo-fin-entry">{body}<span /></div></li>;
}

function Movements({ movements, places, converter, target, onError }) {
  const orderScope = useOrderScope();
  const groups = [];
  for (const movement of movements) {
    const key = Math.floor(movement.at / DAY);
    if (!groups.length || groups[groups.length - 1].key !== key) groups.push({ key, at: movement.at, rows: [] });
    groups[groups.length - 1].rows.push(movement);
  }
  return <div className="xo-fin-ledger">{groups.map((group) => <section className="xo-fin-day" key={group.key}>
    <header><time dateTime={new Date(group.at).toISOString()}>{longDay(group.at)}</time><span>{NUMBER.format(group.rows.length)}</span></header>
    <ol>{group.rows.map((movement) => <MovementRow key={`${movement.kind}-${movement.id}`} movement={movement} place={places.get(movement.officeId)}
      converter={converter} target={target} orderScope={orderScope} onError={onError} />)}</ol>
  </section>)}</div>;
}

const KIND_TABS = Object.freeze(['', 'payment', 'refund', 'invoice', 'shipping']);
// The channels a kind of movement can have.
const SOURCES_BY_KIND = Object.freeze({
  payment: ['storefront', 'mobile', 'portal'], refund: ['storefront', 'mobile', 'portal'], invoice: ['customer'],
  shipping: ['order', 'stock_request', 'standalone'],
});
const STATUS_BY_KIND = Object.freeze({
  '': [], payment: [], refund: [],
  invoice: ['due', 'overdue', 'paid'], shipping: ['label_printed', 'dispatched', 'in_transit', 'delivered', 'returned', 'lost', 'cancelled'],
});

function Ledger({ days, options, converter, target, fixedKind = null, fixedSources = null, title = null, detail = null }) {
  const [kind, setKind] = useState(fixedKind ?? '');
  const [place, setPlace] = useState('');
  const [source, setSource] = useState('');
  const [status, setStatus] = useState('');
  const [currency, setCurrency] = useState('');
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const limit = 50;
  const filters = useMemo(() => ({ ...windowOf(days), kind: kind || undefined, office: place, source: source || (fixedSources ?? undefined), status, currency,
    q: query, limit, offset }), [currency, days, fixedSources, kind, offset, place, query, source, status]);
  const load = useCallback(() => loadFinanceLedger(filters), [filters]);
  const ledger = useLiveResource(['orders', 'organizations', 'shipments'], load);
  const places = useMemo(() => new Map(options.places.map((entry) => [entry.id, entry])), [options.places]);
  const reset = (change) => (value) => { change(value); setOffset(0); };
  const statuses = STATUS_BY_KIND[kind] ?? [];
  async function exportFile() {
    setBusy(true); setError('');
    try { await downloadFinanceLedger({ ...filters, limit: undefined, offset: undefined }); } catch (caught) { setError(errorMessage(caught)); } finally { setBusy(false); }
  }
  const total = ledger.data?.total ?? 0;
  return <section className="xo-cust-surface xo-fin-surface">
    <header className="xo-cust-surface-head">
      <div><h2>{title ?? f('ledger_title')}</h2><p>{detail ?? f('ledger_detail')}</p></div>
      <button type="button" className="xo-btn-2" onClick={exportFile} disabled={busy || total === 0}><Download aria-hidden="true" />{f('export')}</button>
    </header>
    <div className="xo-fin-filters">
      <form className="xo-fin-search" onSubmit={(event) => { event.preventDefault(); setQuery(draft.trim()); setOffset(0); }}>
        <SearchField value={draft} onChange={setDraft} placeholder={f('search')} />
      </form>
      {fixedKind === null && <WorkspaceTabs className="xo-cust-filter" value={kind} label={f('kind_label')}
        onChange={(value) => { setKind(value); setStatus(''); setSource(''); setOffset(0); }}
        tabs={KIND_TABS.map((value) => ({ value, label: value === '' ? f('kind_all') : f(`kinds.${value}`) }))} />}
      <div className="xo-fin-selects">
        <XomicsSelect value={place} onChange={(event) => reset(setPlace)(event.target.value)} aria-label={f('place')}>
          <option value="">{f('all_places')}</option>
          {options.places.map((entry) => <option key={entry.id} value={String(entry.id)}>{placeLabel(entry)}</option>)}
          <option value="none">{f('no_place')}</option>
        </XomicsSelect>
        {fixedSources === null && <XomicsSelect value={source} onChange={(event) => reset(setSource)(event.target.value)} aria-label={f('channel_label')}>
          <option value="">{f('all_channels')}</option>
          {(SOURCES_BY_KIND[kind] ?? options.sources).map((code) => <option key={code} value={code}>{f(`channel.${code}`)}</option>)}
        </XomicsSelect>}
        {statuses.length > 0 && <XomicsSelect value={status} onChange={(event) => reset(setStatus)(event.target.value)} aria-label={f('status_label')}>
          <option value="">{f('all_statuses')}</option>
          {statuses.map((code) => <option key={code} value={code}>{f(`status.${code}`)}</option>)}
        </XomicsSelect>}
        <XomicsSelect value={currency} onChange={(event) => reset(setCurrency)(event.target.value)} aria-label={f('currency_label')}>
          <option value="">{f('all_currencies')}</option>
          {options.currencies.map((entry) => <option key={entry.code} value={entry.code}>{entry.code}</option>)}
        </XomicsSelect>
      </div>
    </div>
    {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
    {ledger.error ? <Failed message={ledger.error} onRetry={ledger.refresh} />
      : ledger.data === null ? <Loading label={f('loading')} />
        : ledger.data.movements.length === 0 ? <div className="xo-cust-empty is-flat"><Empty title={f('ledger_empty')} detail={f('ledger_empty_detail')} /></div>
          : <>
            <Movements movements={ledger.data.movements} places={places} converter={converter} target={target} onError={setError} />
            <footer className="xo-fin-pager">
              <span>{f('showing', { from: NUMBER.format(offset + 1), to: NUMBER.format(Math.min(offset + limit, total)), total: NUMBER.format(total) })}</span>
              <div>
                <button type="button" className="xo-btn-2" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>
                  <ChevronLeft aria-hidden="true" />{f('previous')}</button>
                <button type="button" className="xo-btn-2" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>
                  {f('next')}<ChevronRight aria-hidden="true" /></button>
              </div>
            </footer>
          </>}
  </section>;
}

// ------------------------------------------------------------------ receivables and costs

function Receivables({ figures, converter, days, options, target }) {
  const fmt = (minor) => converter.format(minor);
  return <>
    <Window big={{ label: f('owed_now'), value: fmt(figures.open), note: figures.overdue > 0 ? f('overdue_part', { amount: fmt(figures.overdue) }) : f('none_overdue') }}
      cells={figures.ages.slice(0, 3).map((age) => ({ label: age.label, value: fmt(age.value), note: f(age.count === 1 ? 'invoices_n_one' : 'invoices_n', { count: age.note }) }))} />
    <div className="xo-fin-two">
      <Card title={f('ages')} detail={f('ages_detail')}><Bars rows={figures.ages} format={fmt} empty={f('nothing_owed')} /></Card>
      <Card title={f('debtors')} detail={f('debtors_detail')}>
        {figures.debtors.length === 0 ? <p className="xo-fin-quiet">{f('nothing_owed')}</p> : <ol className="xo-fin-bars">{figures.debtors.map((row) => <li key={row.key}>
          <span className="xo-fin-bar-name"><Link to={`/xomics/organizations?customer=${row.id}`}><strong>{row.label}</strong></Link><small>{row.sub}</small></span>
          <span className="xo-fin-bar-track" aria-hidden="true"><i style={{ width: `${share(row.value, figures.debtors[0].value) * 100}%` }} /></span>
          <span className="xo-fin-bar-value">{fmt(row.value)}<small className={row.overdue ? 't-bad' : ''}>
            {row.overdue ? f('overdue_invoices', { count: row.overdue }) : f('open_invoices', { count: row.invoices })}</small></span>
        </li>)}</ol>}
      </Card>
    </div>
    <Ledger days={days} options={options} converter={converter} target={target} fixedKind="invoice" fixedSources="customer"
      title={f('invoices_title')} detail={f('invoices_detail')} />
  </>;
}

function Costs({ figures, converter, days, options, target }) {
  const fmt = (minor) => converter.format(minor);
  return <>
    <Window big={{ label: f('shipping_costs'), value: fmt(figures.shippingCost), note: f('labels_count', { count: NUMBER.format(figures.labels) }) }} cells={[
      { label: f('per_label'), value: figures.labels ? fmt(figures.shippingCost / figures.labels) : fmt(0), note: f('per_label_note') },
      { label: f('charged_customers'), value: fmt(figures.shippingCharged), note: f('charged_note') },
      { label: f('shipping_cover'), value: figures.shippingCost ? PERCENT.format(figures.shippingCharged / figures.shippingCost) : '0%',
        note: figures.shippingCharged >= figures.shippingCost ? f('covered') : f('not_covered', { amount: fmt(figures.shippingCost - figures.shippingCharged) }),
        tone: figures.shippingCharged >= figures.shippingCost ? 'ok' : 'warn' },
    ]} />
    <div className="xo-fin-two">
      <Card title={f('by_carrier')} detail={f('by_carrier_detail')}>
        <Bars rows={figures.carriers.map((row) => ({ ...row, note: f('labels_count', { count: NUMBER.format(row.count) }) }))} format={fmt} empty={f('no_labels')} /></Card>
      <Card title={f('by_purpose')} detail={f('by_purpose_detail')}>
        <Bars rows={figures.purposes.map((row) => ({ ...row, note: f('labels_count', { count: NUMBER.format(row.count) }) }))} format={fmt} empty={f('no_labels')} /></Card>
    </div>
    <Card title={f('shipping_by_place')} detail={f('shipping_by_place_detail')}>
      <Bars rows={figures.byPlace.filter((row) => row.shipping > 0).map((row) => ({ key: row.key, label: placeTitle(row.place),
        sub: placeSub(row.place), value: row.shipping }))} format={fmt} empty={f('no_labels')} />
    </Card>
    <Ledger days={days} options={options} converter={converter} target={target} fixedKind="shipping" title={f('labels_title')} detail={f('labels_detail')} />
  </>;
}

// ------------------------------------------------------------------ the page

const VIEWS = Object.freeze({
  overview: { title: 'title_overview', subtitle: 'subtitle_overview' },
  ledger: { title: 'title_ledger', subtitle: 'subtitle_ledger' },
  receivables: { title: 'title_receivables', subtitle: 'subtitle_receivables' },
  costs: { title: 'title_costs', subtitle: 'subtitle_costs' },
});

export default function XomicsFinance({ view = 'overview' }) {
  const { user } = useXomicsAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const days = PERIODS.includes(Number(searchParams.get('days'))) ? Number(searchParams.get('days')) : 30;
  const [place, setPlace] = useState('');
  const [channel, setChannel] = useState('');
  const optionsResource = useLiveResource(['locations'], useCallback(() => loadFinanceOptions(), []));
  const loadSummary = useCallback(() => loadFinanceSummary(windowOf(days)), [days]);
  const summary = useLiveResource(['orders', 'organizations', 'shipments'], loadSummary, { enabled: view !== 'ledger' });
  const options = optionsResource.data;
  const [chosen, setChosen] = useState(null);
  const target = chosen ?? user?.preferredCurrency ?? 'EUR';
  const converter = useConverter(target);
  const figures = useFigures(view === 'ledger' ? null : summary.data, options ?? { places: [] }, converter, { place, channel });
  const meta = VIEWS[view] ?? VIEWS.overview;
  const setDays = (value) => { const next = new URLSearchParams(searchParams); next.set('days', String(value)); setSearchParams(next, { replace: true }); };
  const head = <Head title={f(meta.title)} subtitle={f(meta.subtitle)}>
    <PeriodTabs days={days} onChange={setDays} />
    {options && <ShowIn value={target} options={options.currencies} onChange={setChosen} />}
  </Head>;
  if (optionsResource.error) return <div className="xo-platform-page xo-fin-page">{head}<Failed message={optionsResource.error} onRetry={optionsResource.refresh} /></div>;
  if (!options || !converter.ready) return <div className="xo-platform-page xo-fin-page">{head}<Loading label={f('loading')} /></div>;
  const filters = view === 'ledger' ? null : <div className="xo-fin-scope">
    <XomicsSelect value={place} onChange={(event) => setPlace(event.target.value)} aria-label={f('place')}>
      <option value="">{f('all_places')}</option>
      {options.places.filter((entry) => entry.kind !== 'warehouse' || view === 'costs').map((entry) => <option key={entry.id} value={String(entry.id)}>{placeLabel(entry)}</option>)}
    </XomicsSelect>
    {view === 'overview' && <XomicsSelect value={channel} onChange={(event) => setChannel(event.target.value)} aria-label={f('channel_label')}>
      <option value="">{f('all_channels')}</option>
      {CHANNELS.map((code) => <option key={code} value={code}>{f(`channel.${code}`)}</option>)}
    </XomicsSelect>}
    <RateNote converter={converter} target={target} />
  </div>;
  let body;
  if (view === 'ledger') {
    body = <><div className="xo-fin-scope"><RateNote converter={converter} target={target} /></div>
      <Ledger days={days} options={options} converter={converter} target={target} /></>;
  } else if (summary.error) body = <Failed message={summary.error} onRetry={summary.refresh} />;
  else if (!figures) body = <Loading label={f('loading')} />;
  else if (view === 'receivables') body = <Receivables figures={figures} converter={converter} days={days} options={options} target={target} />;
  else if (view === 'costs') body = <Costs figures={figures} converter={converter} days={days} options={options} target={target} />;
  else body = <Overview figures={figures} converter={converter} target={target} />;
  return <div className="xo-platform-page xo-fin-page">
    {head}
    {filters}
    {body}
  </div>;
}

